import { useState, useEffect, useCallback } from "react";
import { useAuthenticatedApi } from "./useAuthenticatedApi";

export interface ScheduleInterval {
  intervalid: string;
  hotelid: string;
  starttime: string; // HH:MM:SS
  endtime: string; // HH:MM:SS
  description: string | null;
  isactive?: boolean;
  createdat?: string;
  updatedat?: string;
}

interface UseScheduleIntervalsOptions {
  hotelId: string;
  limit?: number; // default 100
}

interface UseScheduleIntervalsReturn {
  intervals: ScheduleInterval[];
  isLoading: boolean;
  error: Error | null;
  refetchIntervals: () => Promise<void>;
  findOrCreateIntervalId: (
    startTime: string,
    endTime: string
  ) => Promise<string>;
}

// Helper to ensure time stored as HH:MM:SS (backend expects seconds)
const formatTimeToHHMMSS = (time: string): string => {
  // Accept HH:MM or HH:MM:SS
  if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(time)) {
    return `${time}:00`;
  }
  if (/^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.test(time)) {
    return time;
  }
  console.warn(
    `Invalid time format for HH:MM(:SS) => ${time}. Defaulting 00:00:00`
  );
  return "00:00:00";
};

export const useScheduleIntervals = ({
  hotelId,
  limit = 100,
}: UseScheduleIntervalsOptions): UseScheduleIntervalsReturn => {
  const { callApi } = useAuthenticatedApi();
  const [intervals, setIntervals] = useState<ScheduleInterval[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchIntervals = useCallback(async () => {
    if (!hotelId) return;
    setIsLoading(true);
    setError(null);
    try {
      const cappedLimit = Math.min(limit, 100);
      const resp = await callApi({
        method: "GET",
        url: `/management/schedule-intervals`,
        params: { page: 1, limit: cappedLimit },
      });
      const raw = resp.data.data || [];
      const mapped: ScheduleInterval[] = raw.map((r: any) => ({
        intervalid: r.intervalid || r.id,
        hotelid: r.hotelid || r.hotelId,
        starttime: r.starttime || r.startTime,
        endtime: r.endtime || r.endTime,
        description: r.description ?? null,
        isactive: r.isactive !== undefined ? r.isactive : true,
        createdat: r.createdat || r.createdAt,
        updatedat: r.updatedat || r.updatedAt,
      }));
      setIntervals(mapped);
    } catch (err) {
      console.error("Failed to fetch schedule intervals:", err);
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch schedule intervals")
      );
      setIntervals([]);
    } finally {
      setIsLoading(false);
    }
  }, [hotelId, limit, callApi]);

  useEffect(() => {
    fetchIntervals();
  }, [fetchIntervals]);

  const findOrCreateIntervalId = useCallback(
    async (startTime: string, endTime: string): Promise<string> => {
      if (!hotelId) throw new Error("Hotel ID not provided");

      const formattedStart = formatTimeToHHMMSS(startTime);
      const formattedEnd = formatTimeToHHMMSS(endTime);

      // Fetch fresh intervals from API to avoid stale closure state
      // This ensures we're checking against the most up-to-date intervals
      const cappedLimit = Math.min(100, 100);
      const fetchResp = await callApi({
        method: "GET",
        url: `/management/schedule-intervals`,
        params: { page: 1, limit: cappedLimit },
      });
      const raw = fetchResp.data.data || [];
      const freshIntervals: ScheduleInterval[] = raw.map((r: any) => ({
        intervalid: r.intervalid || r.id,
        hotelid: r.hotelid || r.hotelId,
        starttime: r.starttime || r.startTime,
        endtime: r.endtime || r.endTime,
        description: r.description ?? null,
        isactive: r.isactive !== undefined ? r.isactive : true,
        createdat: r.createdat || r.createdAt,
        updatedat: r.updatedat || r.updatedAt,
      }));
      
      // Now check with fresh intervals
      const existing = freshIntervals.find(
        (i) =>
          i.starttime === formattedStart &&
          i.endtime === formattedEnd &&
          i.hotelid === hotelId
      );
      
      if (existing) {
        // Update local state cache as well
        setIntervals((prev) => {
          const exists = prev.find(i => i.intervalid === existing.intervalid);
          return exists ? prev : [...prev, existing];
        });
        return existing.intervalid;
      }

      // Otherwise create new
      const intervalPayload = {
        startTime: formattedStart,
        endTime: formattedEnd,
        description: `Room Service Menu Hours ${formattedStart}-${formattedEnd}`,
      };
      
      const createResp = await callApi({
        method: "POST",
        url: `/management/schedule-intervals`,
        data: intervalPayload,
      });

      const data = createResp.data.data;
      const newId = data?.intervalid || data?.id;
      if (!newId) throw new Error("Failed to create schedule interval");

      const newInterval = {
        intervalid: newId,
        hotelid: data.hotelid || data.hotelId,
        starttime: data.starttime || data.startTime,
        endtime: data.endtime || data.endTime,
        description: data.description ?? null,
        isactive: data.isactive ?? true,
        createdat: data.createdat || data.createdAt,
        updatedat: data.updatedat || data.updatedAt,
      };

      // Update local cache
      setIntervals((prev) => [...prev, newInterval]);

      return newId;
    },
    [hotelId, intervals, callApi, fetchIntervals]
  );

  return {
    intervals,
    isLoading,
    error,
    refetchIntervals: fetchIntervals,
    findOrCreateIntervalId,
  };
};
